'use client';

import type { EmblaCarouselType } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { Children, isValidElement, type ReactNode, useCallback } from 'react';

import { cx } from '@mage-ui/styled-system/css';
import {
  buttonIconIcon,
  carousel,
  carouselContainer,
  carouselControl,
  carouselControls,
  carouselIndicator,
  carouselIndicators,
  carouselSlide,
  carouselViewport,
} from '@mage-ui/styled-system/recipes';
import { Button, ButtonIcon } from '@/components/buttons';
import { VisuallyHidden } from '@/components/misc';

import { useDotButton, useNavButtons } from './useEmbla';

export type CarouselProps = {
  classNames?: {
    settings?: string;
    root?: string;
    slide?: string;
    container?: string;
    viewport?: string;
    controls?: string;
    control?: string;
    icon?: string;
    indicators?: string;
    indicator?: string;
  };
  withIndicators?: boolean;
  withControls?: boolean;
  loop?: boolean;
  dragFree?: boolean;
  align?: 'start' | 'center' | 'end';
  slidesToScroll?: number | 'auto';
  autoplay?: number;
  previousControl?: {
    label?: string;
    icon?: string;
  };
  nextControl?: {
    label?: string;
    icon?: string;
  };
  indicatorControl?: {
    label?: string;
    ofLabel?: string;
  };
  children?: ReactNode;
};

export const Carousel = ({ classNames, ...props }: CarouselProps) => {
  const plugins = [];
  if (props.autoplay && props.autoplay > 0) {
    plugins.push(
      Autoplay({ delay: props.autoplay ?? 1000, stopOnMouseEnter: true }),
    );
  }

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: props?.align ?? 'center',
      loop: props?.loop ?? true,
      dragFree: props?.dragFree ?? true,
      slidesToScroll: props?.slidesToScroll ?? 'auto',
    },
    plugins,
  );

  const onNavButtonClick = useCallback((emblaApi: EmblaCarouselType) => {
    const autoplay =
      props?.autoplay && props.autoplay > 0
        ? emblaApi?.plugins()?.autoplay
        : null;
    if (!autoplay) return;

    const resetOrStop =
      autoplay.options.stopOnInteraction === false
        ? autoplay.reset
        : autoplay.stop;

    resetOrStop();
  }, []);

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(
    emblaApi as EmblaCarouselType,
    onNavButtonClick,
  );

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = useNavButtons(emblaApi as EmblaCarouselType, onNavButtonClick);

  return (
    <section
      className={cx(classNames?.root ?? carousel(), classNames?.settings)}
      onMouseLeave={() => emblaApi?.plugins().autoplay?.play()}
      aria-label='Carousel'
    >
      <div
        className={classNames?.viewport ?? carouselViewport()}
        ref={emblaRef}
      >
        <div className={classNames?.container ?? carouselContainer()}>
          {Children.map(
            props.children,
            (child) =>
              isValidElement(child) &&
              child.type === Slide && (
                <div className={classNames?.slide ?? carouselSlide()}>
                  {child}
                </div>
              ),
          )}
        </div>
      </div>
      {props?.withControls && props.withControls && (
        <div className={classNames?.controls ?? carouselControls()}>
          <ButtonIcon
            classNames={{
              root: classNames?.control ?? carouselControl(),
              icon: classNames?.icon ?? buttonIconIcon({ size: 'm' }),
            }}
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
            label={props?.previousControl?.label ?? 'Previous'}
            icon={props?.previousControl?.icon ?? 'chevron-left'}
          />
          <ButtonIcon
            classNames={{
              root: classNames?.control ?? carouselControl(),
              icon: classNames?.icon ?? buttonIconIcon({ size: 'm' }),
            }}
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
            label={props?.nextControl?.label ?? 'Next'}
            icon={props?.nextControl?.icon ?? 'chevron-right'}
          />
        </div>
      )}
      {props?.withIndicators && props.withIndicators && (
        <div className={classNames?.indicators ?? carouselIndicators()}>
          {scrollSnaps.map((snap, index) => {
            const totalSlides = Children.toArray(props.children).length;
            return (
              <Button
                key={snap}
                onClick={() => onDotButtonClick(index)}
                classNames={{
                  root: classNames?.indicator ?? carouselIndicator(),
                }}
                aria-current={index === selectedIndex ? true : undefined}
              >
                <VisuallyHidden>
                  {`${props.indicatorControl?.label ?? 'Slide'} ${index + 1} ${
                    props.indicatorControl?.ofLabel ?? 'of'
                  } ${totalSlides}`}
                </VisuallyHidden>
              </Button>
            );
          })}
        </div>
      )}
    </section>
  );
};

const Slide = ({ children }: { children: ReactNode }) => {
  return children;
};

Slide.displayName = 'Carousel.Slide';
Carousel.Slide = Slide;
Carousel.displayName = 'Carousel';
